import { useState, useEffect, useRef } from 'react'
import { Input } from './Input'
import { Button } from './Button'
import { Alert } from './Alert'
import { Modal } from './Modal'
import { useMatterService, Task, Matter } from '../services/matterService'
import { usePaymentService, Payment } from '../services/paymentService'
import { useDocumentService, MatterDocument, DocumentCategory, PepFepCheckResult } from '../services/documentService'
import { useDocumentTemplateService, DocumentTemplate } from '../services/documentTemplateService'
import { useEsignService, NewSigner } from '../services/esignService'
import { Client } from '../services/clientService'
import { matterDisplayStatus } from '../statusStyles'
import { SignatureRequests, SignatureRequestsHandle } from './SignatureRequests'
import { IdentityVerificationPanel } from './IdentityVerificationPanel'
import { SignatureIcon, DownloadIcon } from './icons'
import { ShareLinkPanel } from './ShareLinkPanel'
import { useTerminology } from '../contexts/TerminologyContext'

// Mirrors backend/src/document-execution-page.ts's roleSlug() exactly —
// needed to match a generated document's flat signatureFields (ids like
// "vendor-signature"/"vendor-date") back to the party role each belongs to.
const roleSlug = (role: string): string => role.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

interface MatterDetailProps {
  matter: Matter
  client: Client | undefined
  // Only present when the matter has a secondaryClientId (e.g. joint
  // buyers) — used to offer that client their own portal link too.
  secondaryClient?: Client | undefined
  clientLabel: string
  onBack: () => void
  onMatterUpdated: (matter: Matter) => void
}

const formatDate = (value?: string | null) => {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const formatAmount = (cents: number) => `$${(cents / 100).toFixed(2)}`

const iconButtonClass = 'w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 shrink-0'

const DocumentList: React.FC<{
  documents: MatterDocument[]
  onPreview: (documentId: string) => void
  onDownload: (documentId: string) => void
  // Only passed for Firm Documents — that's the only category signature
  // requests can be created from (see SignatureRequests.tsx's
  // tenantDocuments filter), so Client Documents never gets this button.
  onRequestSignature?: (documentId: string) => void
}> = ({ documents, onPreview, onDownload, onRequestSignature }) => {
  const terminology = useTerminology()
  if (documents.length === 0) {
    return <p className="text-gray-500 text-sm">No documents yet</p>
  }

  return (
    <ul className="divide-y divide-gray-100">
      {documents.map((doc) => (
        <li key={doc.id}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => onPreview(doc.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onPreview(doc.id)
              }
            }}
            className="py-2.5 -mx-2 px-2 rounded-lg flex items-center justify-between gap-3 cursor-pointer hover:bg-gray-50"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{doc.fileName}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {doc.uploadedBy === 'client' ? `Uploaded by ${terminology.client.toLowerCase()}` : 'Uploaded by you'} · {formatDate(doc.createdAt)}
              </p>
            </div>
            {/* Own click purpose (request signature / download) — stops the row's preview click from also firing. */}
            <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
              {onRequestSignature && (
                <button type="button" onClick={() => onRequestSignature(doc.id)} className={iconButtonClass} title="Request Signature" aria-label="Request Signature">
                  <SignatureIcon className="w-4 h-4" />
                </button>
              )}
              <button type="button" onClick={() => onDownload(doc.id)} className={iconButtonClass} title="Download" aria-label="Download">
                <DownloadIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </li>
      ))}
    </ul>
  )
}

export const MatterDetail: React.FC<MatterDetailProps> = ({ matter, client, secondaryClient, clientLabel, onBack, onMatterUpdated }) => {
  const terminology = useTerminology()
  const [tasks, setTasks] = useState<Task[]>([])
  const [tasksLoading, setTasksLoading] = useState(false)
  const [tasksError, setTasksError] = useState<string | null>(null)
  const [newTaskTitle, setNewTaskTitle] = useState('')
  const [addingTask, setAddingTask] = useState(false)

  const [payments, setPayments] = useState<Payment[]>([])
  const [paymentsLoading, setPaymentsLoading] = useState(false)
  const [paymentsError, setPaymentsError] = useState<string | null>(null)

  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState('50.00')
  const [paymentDescription, setPaymentDescription] = useState('Invoice payment')
  const [paymentSubmitting, setPaymentSubmitting] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)

  const [documents, setDocuments] = useState<MatterDocument[]>([])
  const [activeDocTab, setActiveDocTab] = useState<DocumentCategory>('tenant')
  const [documentsLoading, setDocumentsLoading] = useState(false)
  const [documentsError, setDocumentsError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadCategory, setUploadCategory] = useState<DocumentCategory>('tenant')
  const [portalLink, setPortalLink] = useState<{ clientId: string; clientName: string; email?: string; url: string } | null>(null)
  const [portalLinkLoadingClientId, setPortalLinkLoadingClientId] = useState<string | null>(null)
  const [fetchingReports, setFetchingReports] = useState(false)
  const [reportsError, setReportsError] = useState<string | null>(null)
  const [fetchingPepFepCheck, setFetchingPepFepCheck] = useState(false)
  const [pepFepError, setPepFepError] = useState<string | null>(null)
  const [pepFepResults, setPepFepResults] = useState<PepFepCheckResult[] | null>(null)
  const [previewDoc, setPreviewDoc] = useState<MatterDocument | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const reportsPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pepFepPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const generatePollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const signatureRequestsRef = useRef<SignatureRequestsHandle>(null)

  const [templates, setTemplates] = useState<DocumentTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  // "Send for Signature" on a template-generated document skips manual field
  // placement (see documentService.ts's MatterDocument.signatureFields) —
  // this modal just collects who's signing each of the template's declared
  // party roles.
  const [templateSignDoc, setTemplateSignDoc] = useState<MatterDocument | null>(null)
  const [roleAssignments, setRoleAssignments] = useState<Record<string, { name: string; email: string }>>({})
  const [templateSignError, setTemplateSignError] = useState<string | null>(null)
  const [templateSignSubmitting, setTemplateSignSubmitting] = useState(false)

  const { listTasks, createTask, completeTask, closeMatter } = useMatterService()
  const { listPayments, createPayment } = usePaymentService()
  const {
    listDocuments,
    uploadDocument,
    getDownloadUrl,
    getPortalLink,
    fetchGovernmentReports,
    getVicReportsStatus,
    fetchPepFepCheck,
    getPepFepCheckStatus,
  } = useDocumentService()
  const { listTemplates, generateDocument, getGenerationJobStatus } = useDocumentTemplateService()
  const { createSignatureRequest } = useEsignService()

  const loadTasks = async () => {
    setTasksLoading(true)
    setTasksError(null)
    try {
      const results = await listTasks(matter.id)
      setTasks(results.slice().sort((a, b) => a.order - b.order))
    } catch (err) {
      setTasksError(err instanceof Error ? err.message : `Failed to load ${terminology.tasks.toLowerCase()}`)
    } finally {
      setTasksLoading(false)
    }
  }

  const loadPayments = async () => {
    if (!client) return
    setPaymentsLoading(true)
    setPaymentsError(null)
    try {
      const results = await listPayments(client.id)
      setPayments(results)
    } catch (err) {
      setPaymentsError(err instanceof Error ? err.message : 'Failed to load payments')
    } finally {
      setPaymentsLoading(false)
    }
  }

  const loadDocuments = async () => {
    setDocumentsLoading(true)
    setDocumentsError(null)
    try {
      const results = await listDocuments(matter.id)
      setDocuments(results)
    } catch (err) {
      setDocumentsError(err instanceof Error ? err.message : 'Failed to load documents')
    } finally {
      setDocumentsLoading(false)
    }
  }

  const loadTemplates = async () => {
    try {
      const results = await listTemplates()
      setTemplates(results.filter((t) => t.status === 'published' && (!t.matterTypes || t.matterTypes.includes(matter.matterType))))
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Failed to load document templates')
    }
  }

  useEffect(() => {
    loadTasks()
    loadPayments()
    loadDocuments()
    loadTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matter.id, client?.id])

  useEffect(() => () => {
    if (reportsPollTimeoutRef.current) clearTimeout(reportsPollTimeoutRef.current)
    if (pepFepPollTimeoutRef.current) clearTimeout(pepFepPollTimeoutRef.current)
    if (generatePollTimeoutRef.current) clearTimeout(generatePollTimeoutRef.current)
  }, [])

  const handleComplete = async (taskId: string) => {
    setTasksError(null)
    try {
      const task = await completeTask(matter.id, taskId)
      setTasks((prev) => prev.map((t) => (t.id === taskId ? task : t)))
    } catch (err) {
      setTasksError(err instanceof Error ? err.message : `Failed to complete ${terminology.task.toLowerCase()}`)
    }
  }

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault()
    setTasksError(null)
    if (!newTaskTitle.trim()) {
      setTasksError(`Enter a ${terminology.task.toLowerCase()} title`)
      return
    }
    setAddingTask(true)
    try {
      const task = await createTask(matter.id, { title: newTaskTitle })
      setTasks((prev) => [...prev, task])
      setNewTaskTitle('')
    } catch (err) {
      setTasksError(err instanceof Error ? err.message : `Failed to add ${terminology.task.toLowerCase()}`)
    } finally {
      setAddingTask(false)
    }
  }

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    setDocumentsError(null)
    setUploading(true)
    try {
      const document = await uploadDocument(matter.id, file, uploadCategory)
      setDocuments((prev) => [document, ...prev])
    } catch (err) {
      setDocumentsError(err instanceof Error ? err.message : 'Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = async (documentId: string) => {
    setDocumentsError(null)
    try {
      const url = await getDownloadUrl(matter.id, documentId)
      window.open(url, '_blank')
    } catch (err) {
      setDocumentsError(err instanceof Error ? err.message : 'Failed to get download link')
    }
  }

  const handlePreview = async (documentId: string) => {
    const doc = documents.find((d) => d.id === documentId)
    if (!doc) return

    setPreviewDoc(doc)
    setPreviewUrl(null)
    setPreviewError(null)
    setPreviewLoading(true)
    try {
      const url = await getDownloadUrl(matter.id, documentId)
      setPreviewUrl(url)
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Failed to load preview')
    } finally {
      setPreviewLoading(false)
    }
  }

  // One link per (matter, client) — see getOrCreatePortalLinkForClient in
  // backend/src/index.ts. Covers checklist viewing, document upload, and
  // starting ID verification, all from one link (supersedes the old
  // separate "Client Upload Link").
  const handleOpenPortalLinkModal = async (targetClient: Client) => {
    setDocumentsError(null)
    setPortalLinkLoadingClientId(targetClient.id)
    try {
      const { token } = await getPortalLink(matter.id, targetClient.id)
      setPortalLink({
        clientId: targetClient.id,
        clientName: targetClient.fullName || `${targetClient.firstName} ${targetClient.lastName}`.trim(),
        email: targetClient.emailAddress,
        url: `${window.location.origin}/portal/${token}`,
      })
    } catch (err) {
      setDocumentsError(err instanceof Error ? err.message : 'Failed to get portal link')
    } finally {
      setPortalLinkLoadingClientId(null)
    }
  }

  // The fetch itself runs in a backend worker out-of-band (it can take over
  // a minute — see backend/src/vic-property-reports-worker.ts), so this
  // polls the status route rather than waiting on one long request.
  const REPORTS_POLL_INTERVAL_MS = 4000
  const REPORTS_POLL_TIMEOUT_MS = 5 * 60 * 1000

  const pollVicReportsStatus = (deadline: number) => {
    reportsPollTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await getVicReportsStatus(matter.id)

        if (result.status === 'complete') {
          setFetchingReports(false)
          await loadDocuments()
          return
        }

        if (result.status === 'failed') {
          setFetchingReports(false)
          setReportsError(`${result.error ?? 'Failed to fetch property reports'} — please fetch them manually and upload below.`)
          return
        }

        if (Date.now() > deadline) {
          setFetchingReports(false)
          setReportsError('Property report fetch is taking longer than expected — please check back later, or fetch them manually and upload below.')
          return
        }

        pollVicReportsStatus(deadline)
      } catch (err) {
        setFetchingReports(false)
        setReportsError(err instanceof Error ? err.message : 'Failed to check property report status')
      }
    }, REPORTS_POLL_INTERVAL_MS)
  }

  const handleFetchGovernmentReports = async () => {
    setReportsError(null)
    setFetchingReports(true)
    try {
      await fetchGovernmentReports(matter.id)
      pollVicReportsStatus(Date.now() + REPORTS_POLL_TIMEOUT_MS)
    } catch (err) {
      setFetchingReports(false)
      setReportsError(err instanceof Error ? err.message : 'Failed to start property report fetch')
    }
  }

  // Same enqueue-then-poll shape as the VIC property reports fetch above —
  // the actual screening runs in a backend worker out-of-band (see
  // backend/src/pep-fep-check-worker.ts), so this polls the status route
  // rather than waiting on one long request. A per-client error inside
  // pepFepResults doesn't mean the whole check failed — the other client's
  // result can still have come through clean.
  const pollPepFepCheckStatus = (deadline: number) => {
    pepFepPollTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await getPepFepCheckStatus(matter.id)

        if (result.status === 'complete') {
          setFetchingPepFepCheck(false)
          setPepFepResults(result.results)
          await loadDocuments()
          return
        }

        if (result.status === 'failed') {
          setFetchingPepFepCheck(false)
          setPepFepError(result.error ?? 'Failed to run PEP/FEP check')
          return
        }

        if (Date.now() > deadline) {
          setFetchingPepFepCheck(false)
          setPepFepError('PEP/FEP check is taking longer than expected — please check back later.')
          return
        }

        pollPepFepCheckStatus(deadline)
      } catch (err) {
        setFetchingPepFepCheck(false)
        setPepFepError(err instanceof Error ? err.message : 'Failed to check PEP/FEP check status')
      }
    }, REPORTS_POLL_INTERVAL_MS)
  }

  const handleFetchPepFepCheck = async () => {
    setPepFepError(null)
    setPepFepResults(null)
    setFetchingPepFepCheck(true)
    try {
      await fetchPepFepCheck(matter.id)
      pollPepFepCheckStatus(Date.now() + REPORTS_POLL_TIMEOUT_MS)
    } catch (err) {
      setFetchingPepFepCheck(false)
      setPepFepError(err instanceof Error ? err.message : 'Failed to start PEP/FEP check')
    }
  }

  // Same enqueue-then-poll shape as the VIC property reports / PEP-FEP
  // checks above — the actual render runs in a backend worker out-of-band
  // (see backend/src/document-generation-worker.ts).
  const pollGenerationStatus = (jobId: string, deadline: number) => {
    generatePollTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await getGenerationJobStatus(matter.id, jobId)

        if (result.status === 'complete') {
          setGenerating(false)
          await loadDocuments()
          return
        }

        if (result.status === 'failed') {
          setGenerating(false)
          setGenerateError(result.error ?? 'Failed to generate document')
          return
        }

        if (Date.now() > deadline) {
          setGenerating(false)
          setGenerateError('Document generation is taking longer than expected — please check back later.')
          return
        }

        pollGenerationStatus(jobId, deadline)
      } catch (err) {
        setGenerating(false)
        setGenerateError(err instanceof Error ? err.message : 'Failed to check document generation status')
      }
    }, REPORTS_POLL_INTERVAL_MS)
  }

  const handleGenerateDocument = async () => {
    if (!selectedTemplateId) return
    setGenerateError(null)
    setGenerating(true)
    try {
      const { jobId } = await generateDocument(matter.id, selectedTemplateId)
      pollGenerationStatus(jobId, Date.now() + REPORTS_POLL_TIMEOUT_MS)
    } catch (err) {
      setGenerating(false)
      setGenerateError(err instanceof Error ? err.message : 'Failed to start document generation')
    }
  }

  const openTemplateSignModal = (doc: MatterDocument) => {
    const seeded: Record<string, { name: string; email: string }> = {}
    for (const role of doc.partyRoles || []) {
      const isClientRole = role.toLowerCase() === 'client' && client
      seeded[role] = isClientRole ? { name: clientLabel, email: client?.emailAddress || '' } : { name: '', email: '' }
    }
    setRoleAssignments(seeded)
    setTemplateSignError(null)
    setTemplateSignDoc(doc)
  }

  const handleSendTemplateSignature = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!templateSignDoc) return
    setTemplateSignError(null)

    const partyRoles = templateSignDoc.partyRoles || []
    const signatureFields = templateSignDoc.signatureFields || []

    const signers: NewSigner[] = []
    for (const role of partyRoles) {
      const assignment = roleAssignments[role]
      if (!assignment?.name.trim() || !assignment?.email.trim()) {
        setTemplateSignError(`Enter a name and email for ${role}`)
        return
      }
      const slug = roleSlug(role)
      const fields = signatureFields.filter((f) => f.id.startsWith(`${slug}-`))
      signers.push({ name: assignment.name.trim(), email: assignment.email.trim(), fields })
    }

    setTemplateSignSubmitting(true)
    try {
      await createSignatureRequest(matter.id, templateSignDoc.id, signers)
      setTemplateSignDoc(null)
    } catch (err) {
      setTemplateSignError(err instanceof Error ? err.message : 'Failed to send for signature')
    } finally {
      setTemplateSignSubmitting(false)
    }
  }

  const handleCloseMatter = async () => {
    try {
      const updated = await closeMatter(matter.id)
      onMatterUpdated(updated)
    } catch (err) {
      setTasksError(err instanceof Error ? err.message : `Failed to close ${terminology.matter.toLowerCase()}`)
    }
  }

  const handleTakePayment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!client) return
    setPaymentError(null)

    const amountCents = Math.round(Number(paymentAmount) * 100)
    if (!amountCents || amountCents <= 0) {
      setPaymentError('Enter a valid amount')
      return
    }

    setPaymentSubmitting(true)
    try {
      const payment = await createPayment(client.id, {
        amount: amountCents,
        description: paymentDescription,
      })
      setPayments((prev) => [payment, ...prev])
      setShowPaymentModal(false)
    } catch (err) {
      setPaymentError(err instanceof Error ? err.message : 'Failed to take payment')
    } finally {
      setPaymentSubmitting(false)
    }
  }

  const firstOpenIndex = tasks.findIndex((t) => t.status !== 'completed')
  const latestPayment = payments[0]
  const totalCollectedCents = payments.reduce((sum, p) => sum + p.amount, 0)
  const payerLast4 = client?.payerId ? client.payerId.replace(/[^a-zA-Z0-9]/g, '').slice(-4) : null

  const completedTaskCount = tasks.filter((t) => t.status === 'completed').length
  const display = matterDisplayStatus(matter, completedTaskCount, tasks.length)
  const currentTaskCount = firstOpenIndex === -1 ? 0 : 1
  const upcomingTaskCount = tasks.length - completedTaskCount - currentTaskCount
  // The light pill colors read fine on a white card but wash out against the
  // dark header gradient — swap in a translucent chip with a colored dot
  // instead, keyed off the same derived label so the two stay in sync.
  const headerDotClasses: Record<string, string> = {
    'Not Started': 'bg-gray-400',
    'In Progress': 'bg-emerald-400',
    Review: 'bg-red-400',
    Completed: 'bg-accent',
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div
        className="px-6 sm:px-8 py-6 flex items-start justify-between gap-4"
        style={{ background: 'linear-gradient(135deg, #0F2742, #1F3F66)' }}
      >
        <div className="min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="text-xs font-medium text-white/60 hover:text-white mb-2 inline-flex items-center gap-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
            </svg>
            All {terminology.matters.toLowerCase()}
          </button>
          <h2 className="text-2xl font-semibold text-white truncate">
            {clientLabel} &mdash; {matter.matterType}
          </h2>
          <p className="text-sm text-white/60 mt-1 truncate">
            {matter.propertyAddress}
            {matter.settlementDate && ` · Settlement ${formatDate(matter.settlementDate)}`}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <Button
            type="button"
            variant="danger"
            onClick={() => setShowPaymentModal(true)}
            disabled={!client}
          >
            Take Payment
          </Button>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-white">
            <span className={`w-1.5 h-1.5 rounded-full ${headerDotClasses[display.label] || 'bg-gray-400'}`} />
            {display.label}
          </span>
        </div>
      </div>

      <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {tasksError && <Alert type="error" className="mb-4">{tasksError}</Alert>}

          {tasksLoading ? (
            <p className="text-gray-600 text-sm">Loading {terminology.tasks.toLowerCase()}...</p>
          ) : tasks.length === 0 ? (
            <p className="text-gray-600 text-sm">No {terminology.tasks.toLowerCase()} yet</p>
          ) : (
            <ol className="relative">
              {tasks.map((t, i) => {
                const isCompleted = t.status === 'completed'
                const isCurrent = !isCompleted && i === firstOpenIndex
                const isLast = i === tasks.length - 1

                return (
                  <li key={t.id} className="relative pl-9 pb-6 last:pb-0">
                    {!isLast && (
                      <span className="absolute left-[11px] top-6 bottom-0 w-px bg-gray-200" aria-hidden="true" />
                    )}

                    {isCompleted ? (
                      <span className="absolute left-0 top-0 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-3.5 h-3.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                      </span>
                    ) : isCurrent ? (
                      <span className="absolute left-0 top-0 w-6 h-6 rounded-full bg-primary ring-4 ring-primary/15" />
                    ) : (
                      <span className="absolute left-0 top-0 w-6 h-6 rounded-full border-2 border-gray-300 bg-white" />
                    )}

                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className={`font-semibold ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                          {t.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {isCompleted
                            ? `Done · ${formatDate(t.completedAt) || ''}`
                            : t.dueDate
                            ? `Due ${formatDate(t.dueDate)}${isCurrent ? ' · in progress' : ''}`
                            : isCurrent
                            ? 'In progress'
                            : 'No due date'}
                        </p>
                      </div>
                      {!isCompleted && (
                        <Button type="button" size="sm" variant="secondary" onClick={() => handleComplete(t.id)}>
                          Complete
                        </Button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ol>
          )}

          <form onSubmit={handleAddTask} className="flex gap-2 items-end mt-2 pt-4 border-t border-gray-100">
            <div className="flex-1">
              <Input label={`Add custom ${terminology.task.toLowerCase()}`} value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)} />
            </div>
            <Button type="submit" size="sm" disabled={addingTask}>
              {addingTask ? 'Adding...' : 'Add'}
            </Button>
          </form>

          {matter.status === 'active' && (
            <div className="mt-6">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseMatter}>
                Close {terminology.matter}
              </Button>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <h3 className="text-sm font-semibold text-gray-900">Documents</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelected}
                />
                {templates.length > 0 && (
                  <>
                    <select
                      value={selectedTemplateId}
                      onChange={(e) => setSelectedTemplateId(e.target.value)}
                      className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                      aria-label="Document template"
                    >
                      <option value="">Generate document…</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={handleGenerateDocument}
                      disabled={generating || !selectedTemplateId}
                    >
                      {generating ? 'Generating…' : 'Generate'}
                    </Button>
                  </>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={handleFetchGovernmentReports}
                  disabled={fetchingReports}
                >
                  {fetchingReports ? 'Fetching... (can take a minute or two)' : 'Fetch Property & Planning PDFs'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={handleFetchPepFepCheck}
                  disabled={fetchingPepFepCheck}
                >
                  {fetchingPepFepCheck ? 'Screening... (can take a minute or two)' : 'Run PEP/FEP Check'}
                </Button>
                {client && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => handleOpenPortalLinkModal(client)}
                    disabled={portalLinkLoadingClientId === client.id || !matter.settlementDate}
                    title={!matter.settlementDate ? "Set this matter's settlement date first — it's the portal's second security factor" : undefined}
                  >
                    {portalLinkLoadingClientId === client.id ? 'Generating...' : `Portal Link — ${clientLabel}`}
                  </Button>
                )}
                {secondaryClient && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => handleOpenPortalLinkModal(secondaryClient)}
                    disabled={portalLinkLoadingClientId === secondaryClient.id || !matter.settlementDate}
                    title={!matter.settlementDate ? "Set this matter's settlement date first — it's the portal's second security factor" : undefined}
                  >
                    {portalLinkLoadingClientId === secondaryClient.id
                      ? 'Generating...'
                      : `Portal Link — ${secondaryClient.fullName || `${secondaryClient.firstName} ${secondaryClient.lastName}`.trim()}`}
                  </Button>
                )}
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value as DocumentCategory)}
                  className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  aria-label="Upload into"
                >
                  <option value="tenant">Firm Documents</option>
                  <option value="client">{terminology.client} Documents</option>
                </select>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>

            {reportsError && (
              <Alert type="error" className="mb-3">
                {reportsError}{' '}
                <a
                  href="https://www.land.vic.gov.au/property-and-parcel-search"
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-medium"
                >
                  Open land.vic.gov.au
                </a>
              </Alert>
            )}

            {generateError && <Alert type="error" className="mb-3">{generateError}</Alert>}

            {pepFepError && <Alert type="error" className="mb-3">{pepFepError}</Alert>}

            {pepFepResults && (
              <Alert type={pepFepResults.some((r) => r.error) ? 'error' : 'success'} className="mb-3">
                <div className="space-y-1">
                  {pepFepResults.map((r) => (
                    <div key={r.clientId}>
                      {r.error
                        ? `${r.clientName}: PEP/FEP check failed — ${r.error}`
                        : `${r.clientName}: PEP/FEP report saved to Firm Documents.`}
                    </div>
                  ))}
                </div>
              </Alert>
            )}

            {documentsError && <Alert type="error" className="mb-3">{documentsError}</Alert>}

            {documentsLoading ? (
              <p className="text-gray-600 text-sm">Loading documents...</p>
            ) : (
              <div>
                <div className="flex gap-1 border-b border-gray-200 mb-3">
                  {([
                    ['tenant', `Firm Documents (${documents.filter((d) => d.category === 'tenant').length})`],
                    ['client', `${terminology.client} Documents (${documents.filter((d) => d.category === 'client').length})`],
                  ] as const).map(([tab, label]) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveDocTab(tab)}
                      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        activeDocTab === tab
                          ? 'border-primary text-primary'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {activeDocTab === 'tenant' ? (
                  <DocumentList
                    documents={documents.filter((d) => d.category === 'tenant')}
                    onPreview={handlePreview}
                    onDownload={handleDownload}
                    onRequestSignature={(documentId) => {
                      const doc = documents.find((d) => d.id === documentId)
                      if (doc?.sourceTemplateId && doc.partyRoles && doc.partyRoles.length > 0) {
                        openTemplateSignModal(doc)
                      } else {
                        signatureRequestsRef.current?.openForDocument(documentId)
                      }
                    }}
                  />
                ) : (
                  <DocumentList
                    documents={documents.filter((d) => d.category === 'client')}
                    onPreview={handlePreview}
                    onDownload={handleDownload}
                  />
                )}
              </div>
            )}
          </div>

          <SignatureRequests
            ref={signatureRequestsRef}
            matterId={matter.id}
            documents={documents}
            defaultSignerName={client ? clientLabel : undefined}
            defaultSignerEmail={client?.emailAddress}
          />

          {client && (
            <div className="mt-8 pt-6 border-t border-gray-100">
              <IdentityVerificationPanel matterId={matter.id} clientId={client.id} personLabel={clientLabel} />
            </div>
          )}
          {secondaryClient && (
            <div className="mt-8 pt-6 border-t border-gray-100">
              <IdentityVerificationPanel
                matterId={matter.id}
                clientId={secondaryClient.id}
                personLabel={secondaryClient.fullName || `${secondaryClient.firstName} ${secondaryClient.lastName}`.trim()}
              />
            </div>
          )}
        </div>

        <div className="bg-gray-50 rounded-lg border border-gray-200 p-5 h-fit">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">{terminology.client}</h3>
          {client ? (
            <>
              <p className="text-gray-900">{clientLabel}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {payerLast4 ? `Pinch payer linked · •••• ${payerLast4}` : 'No Pinch payer linked'}
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-500">{terminology.client} not found</p>
          )}

          {tasks.length > 0 && (
            <>
              <div className="border-t border-gray-200 my-4" />

              <h3 className="text-sm font-semibold text-gray-900 mb-2">{terminology.task} Progress</h3>
              <div className="flex h-2.5 rounded-lg overflow-hidden bg-gray-200" role="img" aria-label={`${completedTaskCount} completed, ${currentTaskCount} in progress, ${upcomingTaskCount} upcoming, out of ${tasks.length} tasks`}>
                {completedTaskCount > 0 && (
                  <div className="bg-primary" style={{ width: `${(completedTaskCount / tasks.length) * 100}%` }} />
                )}
                {currentTaskCount > 0 && (
                  <div className="bg-accent ml-0.5" style={{ width: `${(currentTaskCount / tasks.length) * 100}%` }} />
                )}
                {upcomingTaskCount > 0 && (
                  <div className="bg-gray-300 ml-0.5" style={{ width: `${(upcomingTaskCount / tasks.length) * 100}%` }} />
                )}
              </div>
              <div className="mt-2.5 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1.5 text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" /> Completed
                  </span>
                  <span className="font-medium text-gray-900">{completedTaskCount}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1.5 text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent" /> In progress
                  </span>
                  <span className="font-medium text-gray-900">{currentTaskCount}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1.5 text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300" /> Upcoming
                  </span>
                  <span className="font-medium text-gray-900">{upcomingTaskCount}</span>
                </div>
              </div>
            </>
          )}

          <div className="border-t border-gray-200 my-4" />

          <h3 className="text-sm font-semibold text-gray-900 mb-1">Latest Payment</h3>
          {paymentsError && <Alert type="error" className="mb-2">{paymentsError}</Alert>}
          {paymentsLoading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : latestPayment ? (
            <>
              <p className="text-2xl font-semibold text-accent">
                {formatAmount(latestPayment.amount)}
                <span className="text-sm font-medium text-accent/80"> &middot; {latestPayment.status || 'pending'}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {latestPayment.description || 'Payment'} via Pinch on {formatDate(latestPayment.transactionDate) || formatDate(latestPayment.createdAt)}
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-500">No payments yet</p>
          )}

          {payments.length > 1 && (
            <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-200">
              Total collected: <span className="font-medium text-gray-700">{formatAmount(totalCollectedCents)}</span> across {payments.length} payments
            </p>
          )}
        </div>
      </div>

      <Modal open={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="Take Payment">
        <form onSubmit={handleTakePayment} className="space-y-4">
          {paymentError && <Alert type="error">{paymentError}</Alert>}
          <Input
            label="Amount (AUD)"
            type="number"
            step="0.01"
            min="0.01"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
          />
          <Input
            label="Description"
            value={paymentDescription}
            onChange={(e) => setPaymentDescription(e.target.value)}
          />
          <Button type="submit" className="w-full" disabled={paymentSubmitting}>
            {paymentSubmitting ? 'Charging...' : 'Take Payment'}
          </Button>
        </form>
      </Modal>

      <Modal
        open={!!previewDoc}
        onClose={() => { setPreviewDoc(null); setPreviewUrl(null); setPreviewError(null) }}
        title={previewDoc?.fileName || 'Preview'}
        size="lg"
      >
        {previewLoading && <p className="text-sm text-gray-500">Loading preview...</p>}
        {previewError && <Alert type="error">{previewError}</Alert>}
        {previewUrl && (
          <iframe src={previewUrl} title={previewDoc?.fileName || 'Document preview'} className="w-full h-[75vh] border-0 rounded-lg" />
        )}
      </Modal>

      <Modal
        open={!!templateSignDoc}
        onClose={() => setTemplateSignDoc(null)}
        title={templateSignDoc ? `Send "${templateSignDoc.fileName}" for Signature` : 'Send for Signature'}
      >
        {templateSignDoc && (
          <form onSubmit={handleSendTemplateSignature} className="space-y-4">
            {templateSignError && <Alert type="error">{templateSignError}</Alert>}
            <p className="text-sm text-gray-500">
              Signature fields were already placed for you from the document's template — just say who's signing each role.
            </p>
            {(templateSignDoc.partyRoles || []).map((role) => (
              <div key={role} className="grid grid-cols-2 gap-2">
                <Input
                  label={`${role} name`}
                  value={roleAssignments[role]?.name || ''}
                  onChange={(e) => setRoleAssignments((prev) => ({ ...prev, [role]: { ...prev[role], name: e.target.value } }))}
                />
                <Input
                  label={`${role} email`}
                  type="email"
                  value={roleAssignments[role]?.email || ''}
                  onChange={(e) => setRoleAssignments((prev) => ({ ...prev, [role]: { ...prev[role], email: e.target.value } }))}
                />
              </div>
            ))}
            <Button type="submit" className="w-full" disabled={templateSignSubmitting}>
              {templateSignSubmitting ? 'Sending…' : 'Send for Signature'}
            </Button>
          </form>
        )}
      </Modal>

      <Modal
        open={!!portalLink}
        onClose={() => setPortalLink(null)}
        title={portalLink ? `Portal Link — ${portalLink.clientName}` : 'Portal Link'}
      >
        {portalLink && (
          <ShareLinkPanel
            link={portalLink.url}
            defaultSubject={`Your ${matter.matterType.toLowerCase()} portal`}
            defaultMessage={`Hi ${portalLink.clientName},\n\nYou can track progress, upload documents, and complete your ID verification for your ${matter.matterType.toLowerCase()} at ${matter.propertyAddress} using the secure link below:\n\n${portalLink.url}\n\nTo open it you'll be asked to confirm your email and the settlement date, so only you can access it.\n\nThis link stays valid for the life of the matter, so feel free to bookmark it and check back anytime.\n\nThanks!`}
            recipientEmail={portalLink.email}
          />
        )}
      </Modal>
    </div>
  )
}
